/**
 * Date calculation utilities for service scheduling
 * All functions are timezone-aware and use EST/EDT (America/New_York)
 */

import { formatInTimeZone, toZonedTime } from 'date-fns-tz';
import { differenceInMinutes, parseISO, differenceInDays } from 'date-fns';

// Private helper — not exported
function parseServiceTimesToday(service, now) {
  const [startHour, startMin] = service.startTime.split(':').map(Number);
  const [endHour, endMin] = service.endTime.split(':').map(Number);

  const serviceStart = new Date(now);
  serviceStart.setHours(startHour, startMin, 0, 0);

  const serviceEnd = new Date(now);
  serviceEnd.setHours(endHour, endMin, 0, 0);

  return {
    minutesUntilStart: differenceInMinutes(serviceStart, now),
    minutesUntilEnd: differenceInMinutes(serviceEnd, now),
  };
}

// Private helper — not exported
function findTodaySpecial(specialServices, todayDateStr) {
  return specialServices.find(s => s.date === todayDateStr) || null;
}

// Constants
export const TIMEZONE = 'America/New_York';
export const DEFAULT_LAST_SERVICE_END_TIME = '13:00'; // 1:00 PM EST

/**
 * Get the next Sunday date, accounting for current time and special services
 * @param {Object|null} specialService - Special service object if one is upcoming
 * @param {string} timezone - Timezone string (default: America/New_York)
 * @returns {string} Formatted date string (e.g., "January 21, 2026")
 */
export function getNextSundayDate(specialService = null, timezone = TIMEZONE) {
  // Get current time in EST
  const nowEST = toZonedTime(new Date(), timezone);
  const currentDay = nowEST.getDay();
  const currentTime = nowEST.toTimeString().slice(0, 5); // "HH:MM"

  // Determine the last service end time based on service type
  let lastServiceEndTime = DEFAULT_LAST_SERVICE_END_TIME; // Default: 13:00 (1:00 PM)

  if (specialService && currentDay === 0) {
    // If there's a special service TODAY (not in the future), check if it's actually today
    const todayDateStr = formatInTimeZone(nowEST, timezone, 'yyyy-MM-dd');
    const specialServiceDate = specialService.date;

    if (todayDateStr === specialServiceDate) {
      // Special service is today, use its end time
      lastServiceEndTime = specialService.endTime; // "12:00" for combined services
    }
  }

  let daysUntilSunday;

  if (currentDay === 0) {
    // It's Sunday - check if services are over
    if (currentTime < lastServiceEndTime) {
      // Services not over yet, show today
      daysUntilSunday = 0;
    } else {
      // Services are over, show next Sunday
      daysUntilSunday = 7;
    }
  } else {
    // Not Sunday, calculate days until next Sunday
    daysUntilSunday = 7 - currentDay;
  }

  const nextSunday = new Date(nowEST);
  nextSunday.setDate(nowEST.getDate() + daysUntilSunday);

  return formatInTimeZone(nextSunday, timezone, 'MMMM d, yyyy');
}

/**
 * Check if current date is within a week of any special service
 * @param {Array} specialServices - Array of special service objects
 * @param {string} timezone - Timezone string (default: America/New_York)
 * @returns {Object|null} Special service object if one is upcoming, null otherwise
 */
export function checkForUpcomingSpecialService(specialServices, timezone = TIMEZONE) {
  const nowEST = toZonedTime(new Date(), timezone);

  // Get today's date string in EST (YYYY-MM-DD)
  const todayDateStr = formatInTimeZone(nowEST, timezone, 'yyyy-MM-dd');
  const currentTime = formatInTimeZone(nowEST, timezone, 'HH:mm');
  const currentDayOfWeek = nowEST.getDay(); // 0 = Sunday

  // If today is a regular Sunday with services still ongoing, don't preempt it
  // with a future special service card
  const regularServicesOngoing =
    !findTodaySpecial(specialServices, todayDateStr) &&
    currentDayOfWeek === 0 &&
    currentTime < DEFAULT_LAST_SERVICE_END_TIME;

  return specialServices.find(s => {
    const days = differenceInDays(parseISO(s.date), parseISO(todayDateStr));
    if (days === 0) return currentTime < s.endTime;
    return days > 0 && days <= 7 && !regularServicesOngoing;
  }) || null;
}

/**
 * Returns the special service scheduled for today, if any (regardless of whether it has ended).
 * Used to give getNextSundayDate the correct end-time cutoff even after a special service ends.
 */
export function getTodaySpecialService(specialServices, timezone = TIMEZONE) {
  const nowEST = toZonedTime(new Date(), timezone);
  const todayDateStr = formatInTimeZone(nowEST, timezone, 'yyyy-MM-dd');
  return findTodaySpecial(specialServices, todayDateStr);
}

/**
 * Format date string to "Month Day, Year" format
 * @param {string} dateString - ISO date string (YYYY-MM-DD)
 * @param {string} timezone - Timezone string (default: America/New_York)
 * @returns {string} Formatted date string (e.g., "January 21, 2026")
 */
export function formatDisplayDate(dateString, timezone = TIMEZONE) {
  // Parse the ISO date string (treats as local date, not UTC)
  const date = parseISO(dateString);
  // Format using EST timezone to ensure correct date display
  return formatInTimeZone(date, timezone, 'MMMM d, yyyy');
}

/**
 * Get service status based on current time
 * @param {Object} service - Service object with startTime and endTime
 * @param {Date} now - Current date/time as a zoned Date
 * @param {Object} statusConfig - Configuration object with startingSoonMinutes and justFinishedMinutes
 * @returns {'starting-soon' | 'in-progress' | 'just-finished' | null}
 */
export function getServiceStatus(service, now, statusConfig = { startingSoonMinutes: 10, justFinishedMinutes: 10 }) {
  const { minutesUntilStart, minutesUntilEnd } = parseServiceTimesToday(service, now);

  // Check status conditions
  if (minutesUntilStart > 0 && minutesUntilStart <= statusConfig.startingSoonMinutes) {
    return 'starting-soon';
  } else if (minutesUntilStart <= 0 && minutesUntilEnd > 0) {
    return 'in-progress';
  } else if (minutesUntilEnd <= 0 && Math.abs(minutesUntilEnd) <= statusConfig.justFinishedMinutes) {
    return 'just-finished';
  }

  return null; // No status to show
}

/**
 * Check if Zoom button should be visible
 * @param {Object} service - Service object with startTime and endTime
 * @param {Date} now - Current date/time as a zoned Date
 * @returns {boolean} True if button should be shown (10 mins before through end of service)
 */
export function shouldShowZoomButton(service, now) {
  // Validate day/date before checking time
  if (service.date) {
    // Special service - check if today matches the service date
    const todayDateStr = formatInTimeZone(now, TIMEZONE, 'yyyy-MM-dd');
    if (todayDateStr !== service.date) {
      return false; // Not the special service date
    }
  } else {
    // Regular service - check if today is Sunday (day 0)
    if (now.getDay() !== 0) {
      return false; // Not Sunday
    }
  }

  const { minutesUntilStart, minutesUntilEnd } = parseServiceTimesToday(service, now);

  // Show button: 10 minutes before start through end of service
  return (minutesUntilStart > 0 && minutesUntilStart <= 10) || (minutesUntilStart <= 0 && minutesUntilEnd > 0);
}
